import { useState } from "react";
import axios from "axios";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal({ open, onOpenChange }) {
  const { setUser, login } = useAuth();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (mode === "forgot") {
      try {
        await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setSent(true);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
      setLoading(false);
      return;
    }
    try {
      const url = mode === "signin" ? "/auth/login" : "/auth/register";
      const payload = mode === "signin" ? { email: form.email, password: form.password } : form;
      const { data } = await axios.post(`${API}${url}`, payload, { withCredentials: true });
      setUser(data);
      toast.success(mode === "signin" ? `Welcome back${data.name ? `, ${data.name.split(" ")[0]}` : ""}` : "Account created — you're signed in");
      onOpenChange(false);
      setForm({ name: "", email: "", password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink border-white/10 text-parchment sm:max-w-sm" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl" data-testid="auth-modal-title">
            {mode === "signin" ? "Resident sign in" : mode === "register" ? "Create your account" : "Reset your password"}
          </DialogTitle>
          <DialogDescription className="text-mist text-sm">
            {mode === "forgot"
              ? "Enter your account email and we'll send you a secure reset link."
              : "Use the same email as your subscription to manage your plan instantly."}
          </DialogDescription>
        </DialogHeader>

        {mode !== "forgot" && (
          <>
            <button onClick={login} data-testid="auth-google-button" type="button"
              className="w-full flex items-center justify-center gap-2.5 bg-parchment text-charcoal font-semibold text-sm py-3.5 rounded-full hover:bg-terracotta hover:text-parchment transition-colors duration-300 mt-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.1a7.14 7.14 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-1">
              <span className="flex-1 h-px bg-white/10" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">or with email</span>
              <span className="flex-1 h-px bg-white/10" />
            </div>
          </>
        )}

        {mode === "forgot" && sent ? (
          <div className="text-center py-4" data-testid="forgot-sent-message">
            <Mail className="w-10 h-10 text-fern mx-auto" />
            <p className="mt-4 text-sm text-parchment font-medium">Check your inbox</p>
            <p className="mt-2 text-xs text-mist leading-relaxed">
              If an account exists for {form.email}, a reset link is on its way. It works once and expires in 1 hour.
            </p>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-3.5">
          {mode === "register" && (
            <div>
              <Label htmlFor="auth-name" className="text-xs text-mist">Full name</Label>
              <Input id="auth-name" data-testid="auth-name-input" value={form.name} placeholder="Alex Rivera"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim" required minLength={2} />
            </div>
          )}
          <div>
            <Label htmlFor="auth-email" className="text-xs text-mist">Email</Label>
            <Input id="auth-email" type="email" data-testid="auth-email-input" value={form.email} placeholder="you@email.com"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim" required />
          </div>
          {mode !== "forgot" && (
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="auth-password" className="text-xs text-mist">
                  Password {mode === "register" && <span className="text-dim">(8+ characters)</span>}
                </Label>
                {mode === "signin" && (
                  <button type="button" data-testid="auth-forgot-link" onClick={() => { setMode("forgot"); setSent(false); }}
                    className="text-[11px] text-dim hover:text-terracotta transition-colors">
                    Forgot password?
                  </button>
                )}
              </div>
              <Input id="auth-password" type="password" data-testid="auth-password-input" value={form.password}
                placeholder="••••••••" onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim"
                required minLength={mode === "register" ? 8 : 1} />
            </div>
          )}
          <button type="submit" data-testid="auth-submit-button" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-parchment hover:text-charcoal text-parchment font-semibold text-sm py-3.5 rounded-full transition-colors duration-300 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {loading ? "One moment…" : mode === "signin" ? "Sign In with Email" : mode === "register" ? "Create Account" : "Email Me a Reset Link"}
          </button>
        </form>
        )}

        <button type="button" data-testid="auth-toggle-mode"
          onClick={() => { setMode(mode === "signin" ? "register" : "signin"); setSent(false); }}
          className="text-xs text-mist hover:text-terracotta transition-colors text-center w-full">
          {mode === "signin" ? "New here? Create an account" : "Back to sign in"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
